import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Phone as PhoneIcon,
  PhoneIncoming,
  PhoneCall,
  Save,
} from "lucide-react";
import { getOutboundCallUrl, getCurrentUserIdForCalls } from "../lib/apiConfig.js";
import { useGroup } from "../context/GroupContext.js";
import { useEventEmitterContext } from "../context/event-emitter.js";
import { EventEmitterType } from "../context/event-emitter.js";
import { useUserPhoneNumbers } from "../hooks/useUserPhoneNumbers.js";
import TestNumberBadge from "../components/TestNumberBadge.js";
import { phonenumbersAPI } from "../services/api.js";
import { normalizeId } from "../lib/idUtils.js";

const STORAGE_KEY = "phoneNumberConfigs";

const API_BASE = process.env.REACT_APP_AGENT_API_URL || "";

async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem("token");
  const url = API_BASE ? `${API_BASE}${path}` : path;
  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    ...opts,
  });
  const raw = await res.text();
  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {}
  if (!res.ok || data?.success === false) {
    const msg =
      (data && (data.error || data.message)) ||
      (raw && raw.trim()) ||
      `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
  return data ?? {};
}

function loadConfigs() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error(e);
  }
  try {
    const inbound = localStorage.getItem("inboundConfigs");
    if (inbound) {
      const parsed = JSON.parse(inbound);
      const out = {};
      Object.keys(parsed || {}).forEach((num) => {
        const c = parsed[num];
        if (c?.script) {
          out[num] = {
            inboundScriptId: c.script._id,
            inboundScript: c.script,
          };
        }
      });
      if (Object.keys(out).length) return out;
    }
  } catch (_) {}
  return {};
}

function saveConfigs(configs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
}

// Voice options for Make call (ElevenLabs-style for basic stack; Kokoro used when stack is kokoro-vad)
const MAKE_CALL_VOICES = [
  { id: "XrExE9yKIg1WjnnlVkGX", name: "Maya (Default)", language: "en" },
  { id: "AZnzlk1XvdvUeBnXmlld", name: "Bella", language: "en" },
  { id: "21m00Tcm4TlvDq8ikWAM", name: "Rachel", language: "en" },
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Antoni", language: "en" },
  { id: "ErXwobaYiN019PkySvjV", name: "Adam", language: "en" },
  { id: "zmcVlqmyk3Jpn5AVYcAL", name: "Sweet", language: "en" },
  { id: "af_heart", name: "Heart (Kokoro)", language: "en" },
  { id: "af_bella", name: "Bella (Kokoro)", language: "en" },
  { id: "am_adam", name: "Adam (Kokoro)", language: "en" },
  { id: "blhsbJ47xCbCpTpQwGyK", name: "Miranda (Warm/Southern)", language: "en" },
  { id: "j45mXgB0BR0mIJbdyK09", name: "Daisy", language: "en" },
];

const PHONE_MODES = { INBOUND: "inbound", OUTBOUND: "outbound" };

function mergeConfigsFromDb(allNumbers, scripts, prevConfigs) {
  const next = { ...prevConfigs };
  allNumbers.forEach((item) => {
    if (!item.number) return;
    const scriptIn = scripts.find((s) => normalizeId(s._id) === normalizeId(item.inboundScriptId));
    const scriptOut = scripts.find((s) => normalizeId(s._id) === normalizeId(item.outboundScriptId));
    next[item.number] = {
      ...(next[item.number] || {}),
      inboundScriptId: item.inboundScriptId ?? next[item.number]?.inboundScriptId ?? "",
      inboundScript: scriptIn || next[item.number]?.inboundScript,
      outboundScriptId: item.outboundScriptId ?? next[item.number]?.outboundScriptId ?? "",
      outboundScript: scriptOut || next[item.number]?.outboundScript,
      inboundCollectionIds: item.inboundCollectionIds ?? next[item.number]?.inboundCollectionIds ?? [],
      outboundCollectionIds: item.outboundCollectionIds ?? next[item.number]?.outboundCollectionIds ?? [],
    };
  });
  return next;
}

export default function Phone({ mode }) {
  const { scopeOwnerIds } = useGroup() || {};
  const { eventEmitter } = useEventEmitterContext() || {};
  const { allNumbers, userNumbers, loading: loadingNumbers, error: numbersError, refetch } = useUserPhoneNumbers();
  const [configs, setConfigs] = useState(loadConfigs);
  const hasSeededFromDb = useRef(false);
  const [scripts, setScripts] = useState([]);
  const isInbound = mode === PHONE_MODES.INBOUND;
  const isOutbound = mode === PHONE_MODES.OUTBOUND;
  const pageTitle = isInbound ? "Inbound" : isOutbound ? "Outbound" : "Phone";
  const pageSubtitle = isInbound
    ? "Set inbound agent per number"
    : isOutbound
    ? "Set outbound agent and place calls"
    : "Configure numbers and place calls";
  const [loadingScripts, setLoadingScripts] = useState(true);
  const [collections, setCollections] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [callDestination, setCallDestination] = useState("");
  const [callFirstName, setCallFirstName] = useState("");
  const [callLastName, setCallLastName] = useState("");
  const [callBusinessName, setCallBusinessName] = useState("");
  const [callVoiceId, setCallVoiceId] = useState(MAKE_CALL_VOICES[0].id);
  const [callIntroMessage, setCallIntroMessage] = useState("");
  const [callStarting, setCallStarting] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState(null);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    apiFetch("/api/scripts")
      .then((data) => {
        let list = (data.scripts || [])
          .map((s) => ({ ...s, type: s.type || "Text Prompt" }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        if (currentUser && Number(currentUser.userlevel) === 100) {
          const myId = currentUser.id || currentUser._id || currentUser.userId;
          const allowedIds = scopeOwnerIds && scopeOwnerIds.length > 0 ? scopeOwnerIds : [myId];
          list = list.filter((s) => {
            const owner = typeof s.ownerId === "string" ? s.ownerId.trim().toLowerCase() : s.ownerId;
            return owner === "global" || allowedIds.some((id) => String(s.ownerId) === String(id));
          });
        }
        setScripts(list);
      })
      .catch(() => setScripts([]))
      .finally(() => setLoadingScripts(false));
  }, [currentUser, scopeOwnerIds]);

  useEffect(() => {
    const ownerId = currentUser?.id || currentUser?._id || currentUser?.userId;
    const query = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : "";
    apiFetch(`/api/knowledgebase-collections${query}`)
      .then((data) => {
        if (data.success && Array.isArray(data.collections)) {
          let list = data.collections;
          if (currentUser && Number(currentUser.userlevel) === 100) {
            const myId = currentUser.id || currentUser._id || currentUser.userId;
            const allowedIds = scopeOwnerIds && scopeOwnerIds.length > 0 ? scopeOwnerIds : [myId];
            list = list.filter((c) => {
              const owner = typeof c.ownerId === "string" ? c.ownerId.trim().toLowerCase() : c.ownerId;
              return owner === "global" || allowedIds.some((id) => String(c.ownerId) === String(id));
            });
          }
          setCollections(list);
        }
      })
      .catch(() => setCollections([]));
  }, [currentUser, scopeOwnerIds]);

  // Pull saved script config from phonenumbers (MongoDB) into configs when numbers + scripts load
  useEffect(() => {
    if (allNumbers.length === 0 || scripts.length === 0) return;
    if (!hasSeededFromDb.current) {
      hasSeededFromDb.current = true;
      setConfigs((prev) => mergeConfigsFromDb(allNumbers, scripts, prev));
    }
  }, [allNumbers, scripts]);

  const updateConfig = (number, patch) => {
    const scriptIds = ["inboundScriptId", "outboundScriptId", "chatbotScriptId"];
    const next = { ...(configs[number] || {}), ...patch };
    scriptIds.forEach((key) => {
      const id = next[key];
      const script = scripts.find((s) => normalizeId(s._id) === normalizeId(id));
      const objKey = key.replace("Id", "");
      if (script) next[objKey] = script;
      else next[objKey] = undefined;
    });
    setConfigs((prev) => {
      const updated = { ...prev, [number]: next };
      saveConfigs(updated);
      return updated;
    });
  };

  const selectedConfig = selectedNumber ? configs[selectedNumber] || {} : null;

  const handleSave = async () => {
    if (!selectedNumber) return;
    const item = allNumbers.find((n) => n.number === selectedNumber);
    if (!item?._id) {
      const message =
        userNumbers.length === 0
          ? "No numbers loaded from the database. Add numbers on the Phone Numbers page, or refresh and try again."
          : "The test number is view-only. Select a number from your list above to save.";
      setSavedFeedback(message);
      setTimeout(() => setSavedFeedback(null), 5000);
      return;
    }
    const cfg = configs[selectedNumber] || {};
    setSavedFeedback(null);
    try {
      if (isInbound) {
        await phonenumbersAPI.update(item._id, {
          inboundScriptId: normalizeId(cfg.inboundScriptId) || null,
          inboundCollectionIds: Array.isArray(cfg.inboundCollectionIds) ? cfg.inboundCollectionIds : [],
        });
      } else {
        await phonenumbersAPI.update(item._id, {
          outboundScriptId: normalizeId(cfg.outboundScriptId) || null,
          outboundCollectionIds: Array.isArray(cfg.outboundCollectionIds) ? cfg.outboundCollectionIds : [],
        });
      }
      const updatedList = await refetch();
      if (Array.isArray(updatedList) && updatedList.length > 0) {
        setConfigs((prev) => mergeConfigsFromDb(updatedList, scripts, prev));
      }
      setSavedFeedback("Saved to database!");
      setTimeout(() => setSavedFeedback(null), 2000);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to save.";
      setSavedFeedback(msg);
      setTimeout(() => setSavedFeedback(null), 4000);
    }
  };

  const startOutboundCall = async () => {
    if (!selectedNumber || !callDestination.trim()) {
      alert("Enter a destination phone number.");
      return;
    }
    const scriptData = selectedConfig?.outboundScript || selectedConfig?.inboundScript;
    setCallStarting(true);
    const hasToken = !!localStorage.getItem("token");
    const placedByUserId = getCurrentUserIdForCalls();
    const outboundUrl = getOutboundCallUrl();
    console.log("[OutboundCall] POST", outboundUrl, "| hasToken:", hasToken, "| scriptId:", scriptData?._id ?? "none");
    try {
      const res = await fetch(outboundUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("token")
            ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
            : {}),
        },
        body: JSON.stringify({
          to: callDestination.trim(),
          from: selectedNumber,
          stack: "kokoro-vad",
          ...(placedByUserId ? { placedByUserId } : {}),
          scriptData: scriptData
            ? {
                _id: scriptData._id,
                scriptName: scriptData.scriptName,
                script: scriptData.script,
                type: scriptData.type,
                firstName: callFirstName.trim() || null,
                lastName: callLastName.trim() || null,
                businessName: callBusinessName.trim() || null,
                aiName: null,
              }
            : null,
          fileId: null,
          voiceId: callVoiceId || null,
          ...(callIntroMessage.trim()
            ? { firstMessage: callIntroMessage.trim(), firstMessageOverride: true }
            : {}),
        }),
      });
      const raw = await res.text();
      console.log("[OutboundCall] Response", res.status, res.statusText, "| body length:", raw?.length ?? 0);
      if (!res.ok) {
        console.warn("[OutboundCall] Non-OK response body (first 500 chars):", raw?.substring(0, 500));
      }
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        console.error("[OutboundCall] Response is not JSON. Full body:", raw?.substring(0, 800));
        if (res.status === 404) {
          alert("Outbound call service not found (404). Is the backend running and the URL correct?");
        } else {
          alert("Server returned an invalid response. The outbound call service may be unavailable.");
        }
        return;
      }
      if (res.ok && data?.success) {
        alert(`Call started. SID: ${data.callSid || "—"}`);
        setCallDestination("");
        setCallFirstName("");
        setCallLastName("");
        setCallBusinessName("");
        setCallVoiceId(MAKE_CALL_VOICES[0].id);
        setCallIntroMessage("");
      } else {
        alert(data?.message || "Failed to start call.");
      }
    } catch (err) {
      console.error("[OutboundCall] Request failed:", err);
      console.error("[OutboundCall] URL was:", getOutboundCallUrl(), "| If 404, check dev server proxy (setupProxy) and that backend (3017) is running.");
      alert("Error starting call. Check the console and that the backend is reachable.");
    } finally {
      setCallStarting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full h-full bg-white overflow-hidden">
      {/* Header - compact */}
      <header className="shrink-0 px-4 md:px-6 py-3 md:py-4 bg-[#f8fafa] rounded-b-xl shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#459EA3] to-[#27AAE1] flex items-center justify-center text-white shadow-md shadow-[#459EA3]/20">
            <PhoneIcon className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">{pageTitle}</h1>
            <p className="text-xs text-gray-500 hidden sm:block">{pageSubtitle}</p>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden flex-col md:flex-row min-h-0">
        {/* Number list - fits height, scrolls if needed */}
        <aside className="w-full md:w-64 lg:w-72 shrink-0 flex flex-col min-h-0 md:min-h-0 max-h-[40vh] md:max-h-none bg-[#f8fafa] rounded-r-2xl overflow-hidden shadow-sm">
          <div className="pt-4 pb-3 px-3 bg-[#eff9f9]/60 shrink-0">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Your numbers
            </p>
          </div>
          {numbersError && (
            <div className="px-3 py-2 bg-amber-50 border-l-2 border-amber-400 rounded-r text-xs text-amber-800">
              {numbersError}
              <button type="button" onClick={() => refetch()} className="ml-1 underline font-medium">Retry</button>
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2 space-y-2">
            {loadingNumbers ? (
              <p className="text-xs text-gray-500 px-2">Loading numbers…</p>
            ) : userNumbers.length === 0 && !numbersError ? (
              <p className="text-xs text-gray-500 px-2">No numbers from database. Only the test number is shown. Add numbers on the Phone Numbers page to save scripts.</p>
            ) : null}
            {!loadingNumbers ? (
              allNumbers.map((item) => {
                const num = item.number;
                const config = configs[num] || {};
                const isSelected = selectedNumber === num;
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setSelectedNumber(num)}
                    className={`w-full text-left rounded-2xl p-4 transition-all duration-200 ${
                      isSelected
                        ? "bg-gradient-to-br from-[#459EA3] to-[#3d8a8e] text-white shadow-lg shadow-[#459EA3]/25"
                        : "bg-white shadow-md hover:shadow-lg hover:bg-gray-50/80 text-gray-800"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          isSelected ? "bg-white/20" : "bg-gray-100"
                        }`}
                      >
                        <PhoneIcon className={`w-5 h-5 ${isSelected ? "text-white" : "text-gray-500"}`} />
                      </div>
                      <span className="font-semibold text-sm truncate">{item.display}</span>
                    </div>
                    <div className="mt-3 flex gap-1.5 flex-wrap items-center">
                      {item.isTest && (
                        <TestNumberBadge className={isSelected ? "[&_span]:bg-white/25 [&_span]:text-white [&_span]:border-white/30" : ""} />
                      )}
                      {isInbound && config.inboundScript && (
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${
                            isSelected ? "bg-white/25 text-white" : "bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          Inbound
                        </span>
                      )}
                      {isOutbound && config.outboundScript && (
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${
                            isSelected ? "bg-white/25 text-white" : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          Outbound
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            ) : null}
          </div>
        </aside>

        {/* Detail panel: scrollable so intro + Start call button are never cut off */}
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-[#f8fafa] rounded-l-2xl rounded-b-2xl shadow-sm md:min-h-0">
          {!selectedNumber ? (
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center px-6">
              <div className="w-16 h-16 rounded-2xl bg-[#DBF4F3]/60 flex items-center justify-center mb-3">
                <PhoneIcon className="w-8 h-8 text-[#459EA3]" />
              </div>
              <p className="text-gray-600 font-medium text-sm">Select a phone number</p>
              <p className="text-xs text-gray-500 mt-1 max-w-xs">
                Choose a number to set the agent and place calls (Outbound) or configure inbound.
              </p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
              <div className="shrink-0 px-4 md:px-5 pt-4 pb-3 bg-white/70">
                <h2 className="text-base font-bold text-gray-900">
                  {allNumbers.find((n) => n.number === selectedNumber)?.display ?? selectedNumber}
                </h2>
                {allNumbers.find((n) => n.number === selectedNumber)?.isTest && (
                  <TestNumberBadge className="mt-1" />
                )}
              </div>
              <div className="flex-1 min-h-0 flex flex-col px-4 md:px-5 pb-8 overflow-y-auto overflow-x-hidden">
                <div className="rounded-2xl bg-white shadow-md flex flex-col min-h-0">
                  <div className="p-5 md:p-6 space-y-5">
                    {isInbound && (
                          <div className="space-y-3">
                            <div>
                              <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                <PhoneIncoming className="w-3.5 h-3.5" />
                                Inbound agent
                              </label>
                              <select
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] bg-gray-50/50"
                                value={normalizeId(selectedConfig?.inboundScriptId) || ""}
                                onChange={(e) =>
                                  updateConfig(selectedNumber, {
                                    inboundScriptId: e.target.value || undefined,
                                  })
                                }
                                disabled={loadingScripts}
                              >
                                <option value="">— None —</option>
                                {scripts.map((s) => (
                                  <option key={normalizeId(s._id)} value={normalizeId(s._id)}>
                                    {s.scriptName}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                Knowledge base (optional)
                              </label>
                              {collections.length === 0 ? (
                                <p className="text-gray-500 text-xs">
                                  No collections.{" "}
                                  <button
                                    type="button"
                                    onClick={() => eventEmitter?.emit({ type: EventEmitterType.DashboardSelectMenu, payload: "Knowledge Base" })}
                                    className="text-[#459EA3] hover:underline font-medium"
                                  >
                                    Create in Knowledge Base
                                  </button>
                                </p>
                              ) : (
                                <select
                                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] bg-gray-50/50"
                                  value={(selectedConfig?.inboundCollectionIds || [])[0] || ""}
                                  onChange={(e) =>
                                    updateConfig(selectedNumber, {
                                      inboundCollectionIds: e.target.value ? [e.target.value] : [],
                                    })
                                  }
                                >
                                  <option value="">— None —</option>
                                  {collections.map((coll) => {
                                    const sid = normalizeId(coll._id) || String(coll._id);
                                    const label = coll.name || coll.collectionName || sid;
                                    return (
                                      <option key={sid} value={sid}>
                                        {label}{coll.fileCount != null ? ` (${coll.fileCount})` : ""}
                                      </option>
                                    );
                                  })}
                                </select>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={handleSave}
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#459EA3] to-[#3d8a8e] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all"
                              >
                                <Save className="w-4 h-4" />
                                Save
                              </button>
                              {savedFeedback && (
                                <span className="text-sm font-medium text-green-600">{savedFeedback}</span>
                              )}
                            </div>
                          </div>
                        )}
                        {isOutbound && (
                          <>
                            <div className="space-y-3">
                              <div>
                                <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                  <PhoneCall className="w-3.5 h-3.5" />
                                  Outbound agent
                                </label>
                                <select
                                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] bg-gray-50/50"
                                  value={normalizeId(selectedConfig?.outboundScriptId) || ""}
                                  onChange={(e) =>
                                    updateConfig(selectedNumber, {
                                      outboundScriptId: e.target.value || undefined,
                                    })
                                  }
                                  disabled={loadingScripts}
                                >
                                  <option value="">— None —</option>
                                  {scripts.map((s) => (
                                    <option key={normalizeId(s._id)} value={normalizeId(s._id)}>
                                      {s.scriptName}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                  Knowledge base (optional)
                                </label>
                                {collections.length === 0 ? (
                                  <p className="text-gray-500 text-xs">
                                    No collections.{" "}
                                    <button
                                      type="button"
                                      onClick={() => eventEmitter?.emit({ type: EventEmitterType.DashboardSelectMenu, payload: "Knowledge Base" })}
                                      className="text-[#459EA3] hover:underline font-medium"
                                    >
                                      Create in Knowledge Base
                                    </button>
                                  </p>
                                ) : (
                                  <select
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] bg-gray-50/50"
                                    value={(selectedConfig?.outboundCollectionIds || [])[0] || ""}
                                    onChange={(e) =>
                                      updateConfig(selectedNumber, {
                                        outboundCollectionIds: e.target.value ? [e.target.value] : [],
                                      })
                                    }
                                  >
                                    <option value="">— None —</option>
                                    {collections.map((coll) => {
                                      const sid = normalizeId(coll._id) || String(coll._id);
                                      const label = coll.name || coll.collectionName || sid;
                                      return (
                                        <option key={sid} value={sid}>
                                          {label}{coll.fileCount != null ? ` (${coll.fileCount})` : ""}
                                        </option>
                                      );
                                    })}
                                  </select>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={handleSave}
                                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#459EA3] to-[#3d8a8e] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all"
                                >
                                  <Save className="w-4 h-4" />
                                  Save
                                </button>
                                {savedFeedback && (
                                  <span className="text-sm font-medium text-green-600">{savedFeedback}</span>
                                )}
                              </div>
                            </div>
                            <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
                              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                                <PhoneCall className="w-3.5 h-3.5" />
                                Test call (optional)
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Destination number</label>
                                  <input
                                    type="tel"
                                    placeholder="+1 234 567 8900"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3]"
                                    value={callDestination}
                                    onChange={(e) => setCallDestination(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Business name</label>
                                  <input
                                    type="text"
                                    placeholder="Business or org name"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3]"
                                    value={callBusinessName}
                                    onChange={(e) => setCallBusinessName(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
                                  <input
                                    type="text"
                                    placeholder="First name"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3]"
                                    value={callFirstName}
                                    onChange={(e) => setCallFirstName(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
                                  <input
                                    type="text"
                                    placeholder="Last name"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3]"
                                    value={callLastName}
                                    onChange={(e) => setCallLastName(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Voice</label>
                                <select
                                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] bg-gray-50/50"
                                  value={callVoiceId}
                                  onChange={(e) => setCallVoiceId(e.target.value)}
                                >
                                  {MAKE_CALL_VOICES.map((v) => (
                                    <option key={v.id} value={v.id}>{v.name} {v.language && `(${v.language})`}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Intro message (optional)</label>
                                <textarea
                                  rows={2}
                                  placeholder="Custom greeting. Leave blank to use default."
                                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#459EA3]/50 focus:border-[#459EA3] resize-y"
                                  value={callIntroMessage}
                                  onChange={(e) => setCallIntroMessage(e.target.value)}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={startOutboundCall}
                                disabled={callStarting || !callDestination.trim()}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#459EA3] to-[#3d8a8e] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                              >
                                <PhoneCall className="w-4 h-4" />
                                {callStarting ? "Starting…" : "Start call"}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
          )}
        </main>
      </div>
    </div>
  );
}
